import { useState } from "react";
import { Wand2 } from "lucide-react";
import { FormField } from "@/components/FormField";
import { PromptResult } from "@/components/PromptResult";
import { VisualTranslationHint } from "@/components/VisualTranslationHint";
import { promptTypes } from "@/data/promptData";
import { buildGeneralPrompt, initialGeneratorForm } from "@/lib/promptBuilders";
import { usePromptLibrary } from "@/hooks/usePromptLibrary";

const fieldRows = [
  ["topic", "Aihe", "Esim. miten yrittäjä voi käyttää AI:ta sisällöntuotannossa"],
  ["audience", "Kohderyhmä", "Esim. suomalaiset yksinyrittäjät ja UGC-tekijät"],
  ["goal", "Tavoite", "Esim. opettaa, myydä, sitouttaa tai herättää keskustelua"],
  ["tone", "Tyyli / tunnelma", "Esim. lämmin, napakka, innostava, asiantunteva"],
  ["channel", "Kanava", "Esim. Instagram, LinkedIn, sähköposti, TikTok"],
  ["length", "Pituus", "Esim. 150 sanaa, 30 sekuntia, 5 kohtaa"],
  ["outcome", "Haluttu lopputulos", "Mitä käyttäjän pitäisi ymmärtää tai tehdä?"],
  ["avoid", "Mitä tulee välttää", "Esim. liian tekninen kieli, kliseet, hype"],
  ["voice", "Brändin tai tekijän ääni", "Esim. rohkaiseva opettaja, selkeä asiantuntija"],
];

export default function GeneratorPage() {
  const [form, setForm] = useState(initialGeneratorForm);
  const [prompt, setPrompt] = useState("");
  const { savePrompt } = usePromptLibrary();

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const generate = () => setPrompt(buildGeneralPrompt(form));
  const showVisualTranslationHint = prompt && ["ai-kuva", "ai-video"].includes(form.promptType);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="generator-page">
      <div className="mb-8 max-w-3xl" data-testid="generator-heading-block">
        <p className="text-sm font-extrabold uppercase text-[#D97757]" data-testid="generator-kicker">Promptigeneraattori</p>
        <h1 className="mt-2 font-heading text-4xl font-bold text-[#2C2F2E] sm:text-5xl" data-testid="generator-title">Täytä tiedot, saat valmiin promptin</h1>
        <p className="mt-4 text-base leading-7 text-[#5C6360]" data-testid="generator-description">Valitse käyttötarkoitus ja kerro tärkeimmät taustat. Studio muotoilee niistä selkeän promptin kopioitavaksi AI-työkaluun.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(420px,0.85fr)]" data-testid="generator-workspace">
        <section className="rounded-[1.75rem] border border-[#E8E5D9] bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] sm:p-7" data-testid="generator-form-section">
          <div className="mb-5" data-testid="generator-form-heading">
            <h2 className="font-heading text-2xl font-bold text-[#2C2F2E]" data-testid="generator-form-title">Täytettävät tiedot</h2>
            <p className="mt-1 text-sm leading-6 text-[#5C6360]" data-testid="generator-form-description">Kerro aihe, kohderyhmä ja tavoite — muut kohdat auttavat tarkentamaan promptia.</p>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="generator-form-grid">
            <div className="md:col-span-2">
              <FormField id="promptType" label="Millaista promptia haluat luoda?" value={form.promptType} onChange={(value) => update("promptType", value)} as="select" options={promptTypes} testId="generator-prompt-type-select" />
            </div>
            {fieldRows.map(([key, label, placeholder]) => (
              <FormField key={key} id={key} label={label} value={form[key]} onChange={(value) => update(key, value)} placeholder={placeholder} testId={`generator-${key}-input`} />
            ))}
            <div className="md:col-span-2">
              <FormField id="notes" label="Erityishuomiot" value={form.notes} onChange={(value) => update("notes", value)} placeholder="Lisää pakolliset pointit, esimerkit, sanat tai rajaukset" as="textarea" testId="generator-notes-textarea" />
            </div>
          </div>
          <button type="button" onClick={generate} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#4A6753] px-6 py-4 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 sm:w-auto" data-testid="generator-generate-button">
            <Wand2 className="h-4 w-4" aria-hidden="true" /> Luo valmis prompti
          </button>
        </section>

        <div data-testid="generator-result-column">
          <PromptResult
            title="Luotu prompti"
            prompt={prompt}
            testIdPrefix="generator-result"
            onSave={({ title, prompt: finalPrompt }) => savePrompt({ title, type: form.promptType || "Promptigeneraattori", prompt: finalPrompt })}
          />
          {showVisualTranslationHint && <VisualTranslationHint testIdPrefix="generator-translation-hint" />}
        </div>
      </div>
    </div>
  );
}